import {FC, useCallback, useEffect, useMemo, useState} from 'react'
import {Button} from '_core'
import {
  checkCompliance,
  selectEditOrder,
  selectIsComplianceChecked,
  selectIsCompliantFetch,
  selectIsDirty,
  selectEditOrderItems as selectOrderItems,
  selectViewOrder,
  setErrors,
  setIsDirty,
  setOrderErrors,
  updateEditOrderWithItems,
} from 'app/features/orders/redux/order/orderEditSlice'
import {useDispatch, useSelector} from 'react-redux'
import {Order, OrderEditSchema} from 'app/features/orders/models/EditOrderState'
import {RootState} from 'setup'
import orderEditService from 'app/services/OrderEditService/OrderEditService'
import {compliantStatusTypes} from 'app/constant'
import {showMessage} from 'app/redux/messageSlice'
import {useNavigate} from 'react-router-dom'
import {eventNames} from 'app/constant'
import eventPublishService from 'app/services/EventPublishService'
import {ViewOrder} from 'app/features/orders/models/OrderState'
import {simpleCurrency} from 'app/format/currency'
import {defaultPaymentErrorMessage} from 'app/constant/payment'
import {selectIsShippingAddressSame} from '../../../../redux/order/orderEditSlice'

interface Props {}

const SaveButton: FC<Props> = () => {
  const dispatch = useDispatch()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const items = useSelector(selectOrderItems)
  const order = useSelector<RootState, Order>(selectEditOrder)
  const viewOrder = useSelector<RootState, ViewOrder>(selectViewOrder)
  const isCompliantChecked = useSelector<RootState, boolean>(selectIsComplianceChecked)
  const isDirty = useSelector<RootState, boolean>(selectIsDirty)
  const [isValid, setIsValid] = useState(false)
  const history = useNavigate()
  const isFetching = useSelector<RootState, boolean>(selectIsCompliantFetch)
  const isShippingAddressSame = useSelector<RootState, boolean>(selectIsShippingAddressSame)

  const refundAmount = useMemo(
    () => parseFloat(viewOrder.totalPrice) - parseFloat(order.totalPrice),
    [order.totalPrice, viewOrder.totalPrice]
  )

  const validateOrder = useCallback(async () => {
    let errors: string[] = []
    try {
      const option: any = {abortEarly: false}
      let validateOrder = {...order, items}
      if (parseFloat(order.shippingPrice) > 0) {
        validateOrder.isFreeShipping = true
      }
      if (order.shippingRate && order.shippingRate.serviceId) {
        validateOrder.isFreeShipping = true
      }
      await OrderEditSchema.validate(validateOrder, option)
    } catch (err: any) {
      errors = err.errors
    }
    const isValid = errors.length === 0
    dispatch(setErrors(errors))
    setIsValid(isValid)
    return isValid
  }, [order, items, dispatch])

  useEffect(() => {
    if (isDirty) {
      validateOrder()
    }
  }, [validateOrder, isDirty])

  const complete = async () => {
    const res = await orderEditService.completeEditOrder(order, true)
    if (res && res.status === 'success') {
      if (isShippingAddressSame) {
        await orderEditService.orderAddressProfileUpdate(order.id)
      }
      await eventPublishService.publish({
        message: {id: order.orderEditId},
        name: eventNames.orderEdit,
      })
      dispatch(setIsDirty(false))
      dispatch(
        showMessage({
          text: 'Your order has been updated successfully',
          variant: 'success',
        })
      )
      history(`/orders/${order.id}/fulfillments/view`)
    } else {
      dispatch(setOrderErrors(res.errors))
      dispatch(
        showMessage({
          text: defaultPaymentErrorMessage(res.errors),
          variant: 'error',
        })
      )
    }
  }

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true)
      await dispatch(updateEditOrderWithItems())
      if (isCompliantChecked && order.isCompliant) {
        await complete()
      } else {
        const {payload: response}: any = await dispatch(checkCompliance())
        if (response && response.complianceStatus === compliantStatusTypes.COMPLIANT) {
          await complete()
        }
      }
    } catch (err) {
      console.error(err)
      dispatch(
        showMessage({
          text: 'Error occurred while processing your request',
          variant: 'error',
        })
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className='me-5'>
      <Button
        id='btnSave'
        type='button'
        title={
          isValid
            ? refundAmount > 0
              ? `Save And Process Refund (${simpleCurrency(refundAmount * -1)})`
              : `Save and Charge Payment Method (${simpleCurrency(refundAmount * -1)})`
            : 'Save'
        }
        isSubmitting={isSubmitting}
        className='btn-primary btn-primary-active'
        onClick={handleSubmit}
        disabled={!isValid || isFetching}
      />
    </div>
  )
}

export default SaveButton
